/**
 * RXNT legacy deep links — use after a successful Integration SDK launch in this browser tab.
 *
 * Flow: first click → SDK auth + POST /ehr-gateway/launch (RXNT cookies in named window).
 * Later clicks (within TTL) → open legacy URLs in the same window name to reuse session.
 *
 * URLs are tenant-specific; confirm paths with RXNT before changing env defaults.
 */
const SESSION_STORAGE_KEY = 'snap_rxnt_ehr_session_until';

/** Named window — reuse same popup across deep-link opens when still open. */
export const RXNT_EHR_WINDOW_NAME = 'rxnt_ehr';

/** Last popup reference (same browser tab only; cleared when user closes the window). */
let rxntPopupRef = null;

const DEFAULT_LEGACY_BASE = 'https://app2.rxnt.com/ehrv9/legacy';

function getLegacyBase() {
  const base = String(process.env.REACT_APP_RXNT_LEGACY_BASE || DEFAULT_LEGACY_BASE).trim();
  return base.replace(/\/$/, '');
}

export function getRxntDeepLinkSessionTtlMs() {
  const mins = Number(process.env.REACT_APP_RXNT_DEEP_LINK_SESSION_MINUTES ?? 20);
  if (!Number.isFinite(mins) || mins <= 0) {
    return 20 * 60 * 1000;
  }
  return mins * 60 * 1000;
}

/** Base64-encode RXNT numeric patient id for PatientId query param (e.g. 293191188 → MjkzMTkxMTg4). */
export function encodeRxntPatientIdForUrl(patientId) {
  if (patientId == null || patientId === '') return null;
  const n = Number(patientId);
  if (!Number.isFinite(n) || n < 1) return null;
  try {
    return btoa(String(n));
  } catch {
    return null;
  }
}

export function buildRxntPatientMedicationsPendingUrl(rxntPatientId) {
  const encoded = encodeRxntPatientIdForUrl(rxntPatientId);
  if (!encoded) return null;
  // ls=pending (not recent/current) + hash route for medications pending queue
  return `${getLegacyBase()}/patientDashboard?PatientId=${encodeURIComponent(
    encoded
  )}&Route=MEDIC&ls=pending#/Medications/Pending`;
}

export function buildRxntPrescriberPendingDashboardUrl() {
  return `${getLegacyBase()}/prescriberDashboard#/dashboard/rx/pending`;
}

export function markRxntBrowserSessionActive() {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(
    SESSION_STORAGE_KEY,
    String(Date.now() + getRxntDeepLinkSessionTtlMs())
  );
}

export function clearRxntBrowserSessionActive() {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(SESSION_STORAGE_KEY);
  rxntPopupRef = null;
}

/** True when the named RXNT popup is still open (closed window = session invalid for deep links). */
export function isRxntPopupOpen() {
  return Boolean(rxntPopupRef && !rxntPopupRef.closed);
}

/**
 * Deep-link session is valid only if TTL not expired AND popup was not closed.
 * (Cannot read popup URL when RXNT shows auth.rxnt.com — cross-origin.)
 */
export function isRxntBrowserSessionActive() {
  if (typeof window === 'undefined') return false;
  const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (!raw) return false;
  const until = Number(raw);
  if (!Number.isFinite(until) || Date.now() > until) {
    clearRxntBrowserSessionActive();
    return false;
  }
  if (rxntPopupRef && rxntPopupRef.closed) {
    clearRxntBrowserSessionActive();
    return false;
  }
  return true;
}

/**
 * window.open feature string — width/height forces a popup window (not a browser tab).
 * Override via REACT_APP_RXNT_EHR_WINDOW_FEATURES (comma-separated, no spaces required).
 */
export function getRxntEhrWindowFeatures() {
  const fromEnv = String(process.env.REACT_APP_RXNT_EHR_WINDOW_FEATURES || '').trim();
  if (fromEnv) {
    return fromEnv;
  }
  if (typeof window === 'undefined') {
    return 'width=1280,height=900,resizable=yes,scrollbars=yes';
  }
  const sw = window.screen?.availWidth ?? 1280;
  const sh = window.screen?.availHeight ?? 900;
  const width = Math.min(1400, Math.max(900, sw - 48));
  const height = Math.min(920, Math.max(640, sh - 80));
  const left = Math.max(0, Math.round((sw - width) / 2));
  const top = Math.max(0, Math.round((sh - height) / 2));
  return `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;
}

/**
 * Open URL in a dedicated RXNT popup window (or navigate existing rxnt_ehr window).
 * @returns {Window|null}
 */
export function openRxntEhrWindow(url) {
  if (!url || typeof window === 'undefined') return null;
  const win = window.open(url, RXNT_EHR_WINDOW_NAME, getRxntEhrWindowFeatures());
  if (win) {
    rxntPopupRef = win;
  }
  return win;
}

/**
 * Open RXNT legacy URL in the shared EHR popup (same session when cookies still valid).
 */
export function openRxntDeepLink(url) {
  if (!url || typeof window === 'undefined') return false;
  const win = openRxntEhrWindow(url);
  if (win) {
    markRxntBrowserSessionActive();
  }
  return Boolean(win);
}

/**
 * @param {'auto'|'patient'|'pending'} target
 * @param {number|null} rxntPatientId
 */
export function resolveRxntDeepLinkUrl(target, rxntPatientId) {
  const mode = target || 'auto';
  if (mode === 'patient' || (mode === 'auto' && rxntPatientId != null)) {
    const url = buildRxntPatientMedicationsPendingUrl(rxntPatientId);
    if (url) return url;
    if (mode === 'patient') return null;
  }
  if (mode === 'pending' || mode === 'auto') {
    return buildRxntPrescriberPendingDashboardUrl();
  }
  return null;
}
